import React, { Fragment } from 'react';
import { ShieldCheck, ShieldAlert, AlertTriangle, ArrowRight, RefreshCw } from 'lucide-react';
interface ScanResultCardProps {
  score?: number;
  status: 'completed' | 'failed';
  errorMessage?: string;
  onViewReport?: () => void;
  onBack?: () => void;
  onRetry?: () => void;
}
export function ScanResultCard({
  score = 0,
  status,
  errorMessage,
  onViewReport,
  onBack,
  onRetry
}: ScanResultCardProps) {
  const isSuccess = status === 'completed';
  let themeColor = '#FF4B4B'; // Default to red for failed
  let riskLabel = 'High Risk\nDetected';
  let Icon = ShieldAlert;
  if (isSuccess) {
    if (score <= 30) {
      themeColor = '#00C18E';
      riskLabel = 'Low Risk\nDetected';
      Icon = ShieldCheck;
    } else if (score <= 70) {
      themeColor = '#FFB800';
      riskLabel = 'Moderate Risk\nDetected';
      Icon = AlertTriangle;
    } else {
      themeColor = '#FF4B4B';
      riskLabel = 'High Risk\nDetected';
      Icon = ShieldAlert;
    }
  }
  return <div className="relative w-full max-w-md bg-[#050505] overflow-hidden p-8 sm:p-10" style={{
    '--theme-color': themeColor,
    '--theme-color-15': `${themeColor}15`,
    '--theme-color-20': `${themeColor}20`,
    border: '1px solid var(--theme-color)',
    boxShadow: '0 0 30px var(--theme-color-15)'
  } as React.CSSProperties} data-id="element-1824" data-mp="10ub97nxkkxps">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none z-0" data-id="element-1825" data-mp="1kjbzbfg6ndiq" />

      {/* Corner Brackets */}
      <div className="absolute top-0 left-0 w-5 h-5 border-t-4 border-l-4 z-10" style={{
      borderColor: themeColor
    }} data-id="element-1826" data-mp="1fjh81ashwlx5" />
      <div className="absolute top-0 right-0 w-5 h-5 border-t-4 border-r-4 z-10" style={{
      borderColor: themeColor
    }} data-id="element-1827" data-mp="3v9k7p8tt8hee" />
      <div className="absolute bottom-0 left-0 w-5 h-5 border-b-4 border-l-4 z-10" style={{
      borderColor: themeColor
    }} data-id="element-1828" data-mp="3g83m65ne7zk0" />
      <div className="absolute bottom-0 right-0 w-5 h-5 border-b-4 border-r-4 z-10" style={{
      borderColor: themeColor
    }} data-id="element-1829" data-mp="3c8mhhefsj6fw" />

      <div className="relative z-10 flex flex-col items-center text-center" data-id="element-1830" data-mp="32fxj9l993p2i">
        {/* Icon */}
        <div className="mb-8 flex justify-center" data-id="element-1831" data-mp="2xixct98hokav">
          <div className="p-5 rounded-full border-2 bg-[#050505]" style={{
          borderColor: 'var(--theme-color)',
          boxShadow: 'inset 0 0 20px var(--theme-color-20)'
        }} data-id="element-1832" data-mp="2sij37mr8p2aq">
            <Icon size={48} color={themeColor} strokeWidth={1.5} data-id="element-1833" data-mp="2jiwadpckt4ta" />
          </div>
        </div>

        {isSuccess ? <>
            <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-8" data-id="element-1834" data-mp="mmyl9xs6f2wa">
              Scan Completed
            </h2>

            <div className="w-full py-6 border-y border-gray-800/60 mb-8 flex items-center justify-center gap-6" data-id="element-1835" data-mp="p8pjdtz70aa6">
              <div className="text-right flex-1" data-id="element-1836" data-mp="u7iujfr0bp76">
                <p className="text-[10px] sm:text-xs text-gray-500 font-mono uppercase tracking-widest mb-2" data-id="element-1837" data-mp="yzsjasgdyj7v">
                  Toxicity Score
                </p>
                <p className="text-4xl sm:text-5xl font-black tracking-tighter" style={{
              color: themeColor
            }} data-id="element-1838" data-mp="13xu016qca6g6">
                  {score}/100
                </p>
              </div>
              <div className="h-16 w-px bg-gray-800/60" data-id="element-1839" data-mp="g7znyrduglwz" />
              <div className="text-left flex-1" data-id="element-1840" data-mp="kao13b9lcezr">
                <p className="text-[10px] sm:text-xs text-gray-500 font-mono uppercase tracking-widest mb-2" data-id="element-1841" data-mp="kbfk7mm04k95">
                  Assessment
                </p>
                <p className="text-lg sm:text-xl font-bold leading-snug" style={{
              color: themeColor
            }} data-id="element-1842" data-mp="1vpje6ssla8e7">
                  {riskLabel.split('\n').map((line, i) => <Fragment key={i} data-id="element-1843" data-mp="1pbdrwzk9132u" data-mpi={i}>
                      {line}
                      {i === 0 && <br data-id="element-1844" data-mp="247lfm9uv8mmv" />}
                    </Fragment>)}
                </p>
              </div>
            </div>

            <div className="w-full space-y-5" data-id="element-1845" data-mp="1za3ayvnvstko">
              <button onClick={onViewReport} className="w-full py-4 font-bold text-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]" style={{
            backgroundColor: themeColor
          }} data-id="element-1846" data-mp="2d6gjvgecnmnj">
                View Full Report
                <ArrowRight size={20} data-id="element-1847" data-mp="2vxhs6v1d0ujr" />
              </button>

              <button onClick={onBack} className="w-full py-2 text-gray-500 hover:text-white font-mono text-xs uppercase tracking-[0.2em] transition-colors" data-id="element-1848" data-mp="2vvn4xa2m8qci">
                Back to Dashboard
              </button>
            </div>
          </> : <>
            <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mb-4" data-id="element-1849" data-mp="31ka6j58fkqzu">
              Scan Failed
            </h2>

            <p className="text-gray-400 font-mono text-sm mb-10 max-w-[250px] mx-auto leading-relaxed" data-id="element-1850" data-mp="36hs98dvuu9ze">
              {errorMessage || 'We encountered an error while processing your conversation data.'}
            </p>

            <div className="w-full space-y-5" data-id="element-1851" data-mp="2wmrx7dqi49rb">
              <button onClick={onRetry} className="w-full py-4 font-bold text-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]" style={{
            backgroundColor: themeColor
          }} data-id="element-1852" data-mp="2iujznjre8erk">
                <RefreshCw size={20} data-id="element-1853" data-mp="2dwim1ecql7nh" />
                Try Again
              </button>

              <button onClick={onBack} className="w-full py-2 text-gray-500 hover:text-white font-mono text-xs uppercase tracking-[0.2em] transition-colors" data-id="element-1854" data-mp="jfihvk41th1b">
                Back to Dashboard
              </button>
            </div>
          </>}
      </div>
    </div>;
}